import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { Expense, ExpenseCreate, ExpenseUpdate, ExpenseFilters, ExpenseSummary } from "@cold-crew/shared";
import type { PaginatedResponse } from "@cold-crew/shared";

// ─── Query Keys ───────────────────────────────────────────────────────────────

export const expenseKeys = {
  all:     () => ["gastos"]                              as const,
  lists:   () => [...expenseKeys.all(), "list"]          as const,
  list:    (f: Partial<ExpenseFilters>) =>
             [...expenseKeys.lists(), f]                 as const,
  detail:  (id: number) =>
             [...expenseKeys.all(), "detail", id]        as const,
  summary: (desde: string, hasta: string) =>
             [...expenseKeys.all(), "summary", desde, hasta] as const,
};

// ─── List ─────────────────────────────────────────────────────────────────────

export function useGastos(filters: Partial<ExpenseFilters> = {}) {
  return useQuery({
    queryKey: expenseKeys.list(filters),
    queryFn:  () =>
      api.get<PaginatedResponse<Expense>>("/gastos", filters as Record<string, any>),
  });
}

// ─── Single ───────────────────────────────────────────────────────────────────

export function useGasto(id: number) {
  return useQuery({
    queryKey: expenseKeys.detail(id),
    queryFn:  () => api.get<Expense>(`/gastos/${id}`),
    enabled:  !!id,
  });
}

// ─── Summary ──────────────────────────────────────────────────────────────────

export function useGastosSummary(desde: string, hasta: string) {
  return useQuery({
    queryKey: expenseKeys.summary(desde, hasta),
    queryFn:  () =>
      api.get<ExpenseSummary>("/gastos/summary", { desde, hasta }),
  });
}

// ─── Create ───────────────────────────────────────────────────────────────────

export function useCreateGasto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ExpenseCreate) => api.post<Expense>("/gastos", data),
    onSuccess:  () => qc.invalidateQueries({ queryKey: expenseKeys.all() }),
  });
}

// ─── Update ───────────────────────────────────────────────────────────────────

export function useUpdateGasto(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ExpenseUpdate) => api.put<Expense>(`/gastos/${id}`, data),
    onSuccess:  (updated) => {
      qc.invalidateQueries({ queryKey: expenseKeys.lists() });
      qc.setQueryData(expenseKeys.detail(id), updated);
    },
  });
}

// ─── Delete ───────────────────────────────────────────────────────────────────

export function useDeleteGasto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/gastos/${id}`),
    onSuccess:  () => qc.invalidateQueries({ queryKey: expenseKeys.all() }),
  });
}

// ─── Restore ─────────────────────────────────────────────────────────────────

export function useRestoreGasto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<Expense>(`/gastos/${id}/restore`),
    onSuccess:  () => qc.invalidateQueries({ queryKey: expenseKeys.all() }),
  });
}
